import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import {
  AppLocale, BAND_FANBASE_NAME,
  BAND_MEMBERSHIP,
  BAND_NAME,
  BAND_NAME_JP,
  BAND_NAME_NUMBER,
  SITE_NAME
} from 'constant';

import { LanguageContext } from 'context/languageContext';

import { getAlbum } from 'utils';

import Layout from 'components/Layout';
import AboutPageSideBar from 'components/AboutPageSideBar';
import ReportErrorContact from 'components/ReportErrorContact';
import MembersTimeline from 'components/MembersTimeline';

import ArrowLink from 'components/ui/ArrowLink';
import Title from 'components/ui/Title';

import buyCoffeeQR from 'assets/bmc-qr.png';
import photo from 'assets/photos/band4.jpg';

import {
  Wrapper,
  BuyCoffeeImage,
  ExplanationBox,
  ExplanationBoxNumber,
  Figure,
  Image
} from './AboutPage.styles';

const AboutPage = () => {
  const intl = useIntl();
  const { locale } = useContext(LanguageContext);

  const variables = {
    bandName: locale === AppLocale.EN ? BAND_NAME : BAND_NAME_JP,
    agency: <b>{intl.formatMessage({ id: 'about.agency' })}</b>,
    taka: <Link to="/about/taka">{intl.formatMessage({ id: 'people.taka' })}</Link>,
    takaFullName: <Link to="/about/taka">{intl.formatMessage({ id: 'people.takaFullName' })}</Link>,
    toruFullName: <Link to="/about/toru">{intl.formatMessage({ id: 'people.toruFullName' })}</Link>,
    ryotaFullName: <Link to="/about/ryota">{intl.formatMessage({ id: 'people.ryotaFullName' })}</Link>,
    tomoyaFullName: <Link to="/about/tomoya">{intl.formatMessage({ id: 'people.tomoyaFullName' })}</Link>,
    albumBeamOfLight: <Link to={getAlbum('beam-of-light', false, locale, 'link')}>{getAlbum('beam-of-light', false, locale, 'name')}</Link>,
    albumKanjoEffect: <Link to={getAlbum('kanjo-effect', false, locale, 'link')}>{getAlbum('kanjo-effect', false, locale, 'name')}</Link>,
    albumNicheSyndrome: <Link to={getAlbum('niche-syndrome', false, locale, 'link')}>{getAlbum('niche-syndrome', false, locale, 'name')}</Link>,
    albumZankyoReference: <Link to={getAlbum('zankyo-reference', false, locale, 'link')}>{getAlbum('zankyo-reference', false, locale, 'name')}</Link>,
    albumJinseiKaketeBokuWa: <Link to={getAlbum('jinsei-kakete-boku-wa', false, locale, 'link')}>{getAlbum('jinsei-kakete-boku-wa', false, locale, 'name')}</Link>,
    album35xxxv: <Link to={getAlbum('35xxxv', false, locale, 'link')}>{getAlbum('35xxxv', false, locale, 'name')}</Link>,
    albumOneokrock: <Link to={getAlbum('oneokrock', false, locale, 'link')}>{getAlbum('oneokrock', false, locale, 'name')}</Link>,
    albumZeitakubyo: <Link to={getAlbum('zeitakubyo', false, locale, 'link')}>{getAlbum('zeitakubyo', false, locale, 'name')}</Link>,
    // @TODO: Change to tour
    albumDetox: <Link to={getAlbum('detox', false, locale, 'link')}>{getAlbum('detox', false, locale, 'name')}</Link>,
  }
  const bandNameInNumber = [
    {
      number: '1',
      reading: 'one',
      japanese: 'ワン'
    },
    {
      number: '0',
      reading: 'o',
      japanese: 'オ'
    },
    {
      number: '9',
      reading: 'ku',
      japanese: 'ク'
    },
    {
      number: '6',
      reading: 'ro',
      japanese: 'ロ'
    },
    {
      number: '9',
      reading: 'kku',
      japanese: 'ック'
    },
  ];

  return (
    <Layout
      sidebar={(<AboutPageSideBar />)}
    >
      <Wrapper>
        <Title size="big" color="secondary">
          {intl.formatMessage({ id: 'about.title' }, { ...variables })}
        </Title>
        <p>
          {intl.formatMessage({ id: 'about.general.description1' }, {
            ...variables,
            highlight: <b>{intl.formatMessage({ id: 'about.general.description1.highlight' }, { ...variables })}</b>
          })}
        </p>
        <Figure>
          <Image src={photo} alt="" />
        </Figure>
        <p>
          {intl.formatMessage({ id: 'about.general.description2' }, { ...variables })}
        </p>
        <p>
          {intl.formatMessage({ id: 'about.general.description3' }, { ...variables })}
        </p>
        <p>
          {intl.formatMessage({ id: 'about.general.description4' }, { ...variables })}
        </p>
        <p>
          {intl.formatMessage({ id: 'about.general.description5' }, { ...variables })}
        </p>
        <Title size="normal" color="black">
          {intl.formatMessage({ id: 'about.earlyYears.title' })}
        </Title>
        <p>
          {intl.formatMessage({ id: 'about.earlyYears.description1' }, { ...variables })}
        </p>
        <Title size="normal" color="black">
          {intl.formatMessage({ id: 'about.earlyYears.title2' })}
        </Title>
        <MembersTimeline />
        <p>
          {intl.formatMessage({ id: 'about.earlyYears.description2' }, { ...variables })}
        </p>
        <ArrowLink to="/about/history" size="normal" color="secondary">
          {intl.formatMessage({ id: 'about.historyLink' }, { ...variables })}
        </ArrowLink>
        <Title size="normal" color="black">
          {intl.formatMessage({ id: 'about.bandName.title1' })}
        </Title>
        <p>
          {intl.formatMessage({ id: 'about.bandName.description1' }, { ...variables })}
        </p>
        <p>
          {intl.formatMessage({ id: 'about.bandName.description2' }, { ...variables })}
        </p>
        <p>
          {intl.formatMessage({ id: 'about.bandName.description3' }, { ...variables })}
        </p>
        <Title size="small" color="black">
          {intl.formatMessage({ id: 'about.bandName.title2' }, {
            bandNameNumber: BAND_NAME_NUMBER
          })}
        </Title>
        <ExplanationBox>
          {
            bandNameInNumber.map((oneNumber) => (
              <ExplanationBoxNumber key={`${oneNumber.number}-${oneNumber.japanese}`}>
                <span>{oneNumber.japanese}</span>
                <b>{oneNumber.number}</b>
                <span>{oneNumber.reading}</span>
              </ExplanationBoxNumber>
            ))
          }
        </ExplanationBox>
        <p>{intl.formatMessage({ id: 'about.bandName.description4' })}</p>
        <Title size="normal" color="black">
          {intl.formatMessage({ id: 'about.international.title' })}
        </Title>
        <p>
          {intl.formatMessage({ id: 'about.international.description1' }, { ...variables })}
        </p>
        <Title size="normal" color="black">
          {intl.formatMessage({ id: 'about.performance.title' })}
        </Title>
        <p>
          {intl.formatMessage({ id: 'about.performance.description1' }, { ...variables })}
        </p>
        <Title size="normal" color="black">
          {intl.formatMessage({ id: 'about.influence.title' })}
        </Title>
        <p>
          {intl.formatMessage({ id: 'about.influence.description1' }, { ...variables })}
        </p>
        <p>
          {intl.formatMessage({ id: 'about.influence.description2' }, { ...variables })}
        </p>
        <Title size="normal" color="black">
          {intl.formatMessage({ id: 'about.fanBase.title' })}
        </Title>
        <p>
          {intl.formatMessage({ id: 'about.fanBase.description1' }, {
           ...variables,
            fanbaseName: BAND_FANBASE_NAME,
            fanbaseNameBold: <b>{BAND_FANBASE_NAME}</b>
          })}
        </p>
        <p>
          {intl.formatMessage({ id: 'about.fanBase.description2' }, {
            ...variables,
            fanbaseName: BAND_FANBASE_NAME
          })}
        </p>
        <p>
          {intl.formatMessage({ id: 'about.fanBase.description3' }, {
            ...variables,
            fanbaseName: BAND_FANBASE_NAME
          })}
        </p>
        <ArrowLink to="/about/awards" size="normal" color="secondary">
          {intl.formatMessage({ id: 'awards.title' })}
        </ArrowLink>
        <hr />
        <Title size="big">
          {intl.formatMessage({ id: 'about.membership.title' }, {
            membership: BAND_MEMBERSHIP
          })}
        </Title>
        <p>
          {intl.formatMessage({ id: 'about.membership.description1' }, {
            ...variables,
            membershipBold: <b>{BAND_MEMBERSHIP}</b>,
            membership: BAND_MEMBERSHIP
          })}
        </p>
        <p>
          {intl.formatMessage({ id: 'about.membership.description2' }, {
            ...variables,
            membership: BAND_MEMBERSHIP
          })}
        </p>
        <p>
          {intl.formatMessage({ id: 'about.membership.description3' }, {
            ...variables,
            membership: BAND_MEMBERSHIP
          })}
        </p>
        <ArrowLink to="https://oneokrock-pf.com/" target="_blank" size="small" color="black">
          {intl.formatMessage({ id: 'about.membership.link' }, {
            membership: BAND_MEMBERSHIP
          })}
        </ArrowLink>
        <hr />
        <Title size="big">
          {intl.formatMessage({ id: 'about.creator.title' }, {
            siteName: SITE_NAME
          })}
        </Title>
        <p>
          {intl.formatMessage({ id: 'about.creator.description1' }, { ...variables })}
        </p>
        <p>
          {intl.formatMessage({ id: 'about.creator.description2' }, { ...variables })}
        </p>
        <Title element="h2" color="black">{intl.formatMessage({ id: 'sidebar.buy' })}</Title>
        <p>
          {intl.formatMessage({ id: 'about.creator.description3' }, { ...variables })}
        </p>
        <BuyCoffeeImage src={buyCoffeeQR} alt="" />
        <p>
          {intl.formatMessage({ id: 'about.creator.description4' }, { ...variables })}
        </p>
        <Title element="h2" color="black">
          {intl.formatMessage({ id: 'about.creator.contact' })}
        </Title>
        <ReportErrorContact showContact />
      </Wrapper>
    </Layout>
  );
};

export default AboutPage;
